import React, { useEffect, useState } from 'react';
import { api, xeroConnectUrl, gmailConnectUrl, myobConnectUrl } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { Button } from '../components/ui/Button';
import { FieldSelect } from '../components/ui/FieldInput';
import { FieldInput } from '../components/ui/FieldInput';
import { Check, Email, Plus, X, XeroX, Sparkle } from '../components/ui/Icons';

type Section = 'xero' | 'myob' | 'email' | 'gmail' | 'automation' | 'billing' | 'profile' | 'account';

const NAV_ITEMS_BASE: { key: Section; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'xero', label: 'Xero connection' },
  { key: 'email', label: 'Email forwarding' },
  { key: 'automation', label: 'Automation' },
  { key: 'billing', label: 'Billing' },
  { key: 'account', label: 'Account' },
];

export function Settings({ userEmailProp }: { userEmailProp?: string } = {}) {
  const isMobile = useIsMobile();
  const [section, setSection] = useState<Section>('profile');
  const [status, setStatus] = useState<{ connected: boolean; connection: { default_bank_account_id: string | null; default_account_code: string | null } | null } | null>(null);
  const [bankAccounts, setBankAccounts] = useState<Array<{ AccountID: string; Code: string; Name: string }>>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<Array<{ AccountID: string; Code: string; Name: string; Type: string }>>([]);
  const [aliases, setAliases] = useState<Array<{ id: string; email: string }>>([]);
  const [newAlias, setNewAlias] = useState('');
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [autoPostThreshold, setAutoPostThreshold] = useState(85);
  const [learnFromCorrections, setLearnFromCorrections] = useState(true);
  const [duplicateDetection, setDuplicateDetection] = useState(true);

  // Billing state
  const [billing, setBilling] = useState<null | {
    plan: 'free' | 'solo' | 'business' | 'pro';
    status?: string;
    current_period_end?: number;
    cancel_at_period_end?: boolean;
    usage: { lifetime: number; period: number; cap: number; overage: number };
  }>(null);
  const [upgradeBusy, setUpgradeBusy] = useState<string | null>(null);

  // Categorisation accuracy dashboard
  const [metrics, setMetrics] = useState<null | {
    window_days: number;
    total: number;
    unedited: number;
    accuracy: number | null;
    needs_review: number;
    sources: { rule: number; llm: number; corpus: number; fuzzy: number };
    top_edited: Array<{ vendor: string; account_code: string; count: number }>;
  }>(null);
  const [refreshingCoa, setRefreshingCoa] = useState(false);
  const [coaMsg, setCoaMsg] = useState<string | null>(null);

  useEffect(() => {
    api.categorisationMetrics().then(setMetrics).catch(() => {});
  }, []);

  async function refreshCoa() {
    setRefreshingCoa(true);
    setCoaMsg(null);
    try {
      const res = await api.refreshCoa();
      setCoaMsg(`Refreshed — ${res.count} expense accounts loaded.`);
    } catch (err) {
      setCoaMsg((err as Error).message);
    } finally {
      setRefreshingCoa(false);
    }
  }

  const forwardingAddress = 'receipts@receiptrec.com';

  async function reload() {
    try {
      const me = await api.me();
      setUserEmail(me.user.email);
    } catch { /* ignore */ }
    const s = await api.xeroStatus();
    setStatus(s);
    if (s.connected) {
      try {
        const [b, a] = await Promise.all([api.xeroBankAccounts(), api.xeroAccounts()]);
        setBankAccounts(b.accounts);
        setExpenseAccounts(a.accounts.filter((acc) => acc.Type === 'EXPENSE' || acc.Type === 'DIRECTCOSTS'));
      } catch (err) {
        console.warn('Could not load Xero accounts', err);
      }
    }
    const al = await api.listEmailAliases();
    setAliases(al.aliases);
    try {
      const b = await api.billingStatus();
      setBilling(b);
    } catch { /* ignore */ }
  }

  async function startCheckout(plan: 'solo' | 'business' | 'pro') {
    setUpgradeBusy(plan);
    try {
      const { url } = await api.billingCheckout(plan);
      window.location.href = url;
    } catch (err) {
      setSaveMsg('Checkout failed. Try again.');
      setUpgradeBusy(null);
    }
  }

  async function openPortal() {
    try {
      const { url } = await api.billingPortal();
      window.location.href = url;
    } catch { setSaveMsg('Could not open billing portal.'); }
  }

  // Gmail state
  const [gmail, setGmail] = useState<null | { connected: boolean; email?: string; last_synced_at?: number }>(null);
  useEffect(() => {
    api.gmailStatus().then(setGmail).catch(() => {});
  }, [billing?.plan]);

  // MYOB state — only shown in NAV_ITEMS when the user has an active MYOB connection.
  // Once we have real credentials, users who connected via the onboarding picker
  // land here; Xero-signup users keep seeing the Xero section.
  const [myobStatus, setMyobStatus] = useState<null | { configured: boolean; connected: boolean; connection: any }>(null);
  const [myobBankAccounts, setMyobBankAccounts] = useState<Array<{ UID: string; DisplayID: string; Name: string; Type: string }>>([]);
  const [myobExpenseAccounts, setMyobExpenseAccounts] = useState<Array<{ UID: string; DisplayID: string; Name: string; Description?: string }>>([]);
  const [myobTaxCodes, setMyobTaxCodes] = useState<Array<{ UID: string; Code: string; Description: string; Rate: number }>>([]);
  useEffect(() => {
    (async () => {
      try {
        const s = await api.myobStatus();
        setMyobStatus(s);
        if (s.connected) {
          const [b, a, t] = await Promise.all([api.myobBankAccounts(), api.myobAccounts(), api.myobTaxCodes()]);
          setMyobBankAccounts(b.accounts);
          setMyobExpenseAccounts(a.accounts);
          setMyobTaxCodes(t.codes);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  async function saveMyobDefaults(body: { default_bank_account_uid?: string; default_account_uid?: string; default_tax_code_uid?: string }) {
    setSaveMsg(null);
    await api.saveMyobDefaults(body);
    setSaveMsg('Saved.');
    try {
      const s = await api.myobStatus();
      setMyobStatus(s);
    } catch { /* ignore */ }
  }

  // Integration nav: show Xero section by default, or MYOB if the user is on MYOB.
  // If they're on neither we still show Xero as the signup default path.
  const onMyob = !!myobStatus?.connected;
  const integrationNav = { key: (onMyob ? 'myob' : 'xero') as Section, label: onMyob ? 'MYOB' : 'Xero connection' };

  const NAV_ITEMS = [
    NAV_ITEMS_BASE[0], // profile
    integrationNav,
    NAV_ITEMS_BASE[2], // email
    { key: 'gmail' as Section, label: 'Gmail' },
    ...NAV_ITEMS_BASE.slice(3),
  ];

  useEffect(() => { void reload(); }, []);

  async function saveDefaults(body: { default_bank_account_id?: string; default_account_code?: string }) {
    setSaveMsg(null);
    await api.saveXeroDefaults(body);
    setSaveMsg('Saved.');
    await reload();
  }

  function copyEmail() {
    navigator.clipboard.writeText(forwardingAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  /* Shared settings content renderer */
  const settingsContent = (
    <>
          {/* Xero section */}
          {section === 'xero' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Xero connection
              </h2>
              {status?.connected ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 16px',
                      background: '#E8F5E3',
                      borderRadius: 10,
                    }}
                  >
                    <XeroX size={16} color={T.xeroBlue} />
                    <span style={{ fontSize: 14, color: T.sageDarker, fontWeight: 500 }}>Connected to Xero</span>
                  </div>

                  <FieldSelect
                    label="Default bank account"
                    value={status.connection?.default_bank_account_id ?? ''}
                    onChange={(v) => saveDefaults({ default_bank_account_id: v })}
                    options={bankAccounts.map((a) => ({ value: a.AccountID, label: `${a.Name} (${a.Code})` }))}
                    placeholder="-- Select bank account --"
                  />

                  <FieldSelect
                    label="Default expense account"
                    value={status.connection?.default_account_code ?? ''}
                    onChange={(v) => saveDefaults({ default_account_code: v })}
                    options={expenseAccounts.map((a) => ({ value: a.Code, label: `${a.Code} - ${a.Name}` }))}
                    placeholder="-- Select expense account --"
                  />

                  {saveMsg && (
                    <span style={{ fontSize: 13, color: T.sageDarker }}>{saveMsg}</span>
                  )}

                  <div style={{ borderTop: `1px solid ${T.paperLine}`, paddingTop: 20 }}>
                    <Button
                      variant="delete"
                      size="small"
                      onClick={async () => {
                        if (!confirm('Disconnect Xero? You will need to reconnect to post receipts.')) return;
                        await api.xeroDisconnect();
                        await reload();
                      }}
                    >
                      Disconnect Xero
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 16 }}>
                    Connect your Xero account to start posting receipts.
                  </p>
                  <a
                    href={xeroConnectUrl()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 24px',
                      background: T.xeroBlue,
                      color: '#FFFFFF',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "'Geist', sans-serif",
                      textDecoration: 'none',
                    }}
                  >
                    <XeroX size={16} color="#FFFFFF" />
                    Connect to Xero
                  </a>
                </div>
              )}
            </div>
          )}

          {/* MYOB section */}
          {section === 'myob' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                MYOB connection
              </h2>
              {myobStatus?.connected ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 16px',
                      background: '#E8F5E3',
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#6B2C91', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>M</div>
                    <span style={{ fontSize: 14, color: T.sageDarker, fontWeight: 500 }}>
                      Connected to MYOB{myobStatus.connection?.company_file_name ? ` — ${myobStatus.connection.company_file_name}` : ''}
                    </span>
                  </div>

                  {myobStatus.connection?.refresh_failed_at && (
                    <div style={{ padding: '10px 14px', background: T.warnBg, borderRadius: 10, fontSize: 13, color: T.warn }}>
                      MYOB re-authentication needed. Reconnect to continue posting receipts.
                    </div>
                  )}

                  <FieldSelect
                    label="Default bank account"
                    value={myobStatus.connection?.default_bank_account_uid ?? ''}
                    onChange={(v) => saveMyobDefaults({ default_bank_account_uid: v })}
                    options={myobBankAccounts.map((a) => ({ value: a.UID, label: `${a.Name} (${a.DisplayID})` }))}
                    placeholder="-- Select bank account --"
                  />

                  <FieldSelect
                    label="Default expense account"
                    value={myobStatus.connection?.default_account_uid ?? ''}
                    onChange={(v) => saveMyobDefaults({ default_account_uid: v })}
                    options={myobExpenseAccounts.map((a) => ({ value: a.UID, label: `${a.DisplayID} – ${a.Name}` }))}
                    placeholder="-- Select expense account --"
                  />

                  <FieldSelect
                    label="Default tax code"
                    value={myobStatus.connection?.default_tax_code_uid ?? ''}
                    onChange={(v) => saveMyobDefaults({ default_tax_code_uid: v })}
                    options={myobTaxCodes.map((t) => ({ value: t.UID, label: `${t.Code} – ${t.Description}` }))}
                    placeholder="-- Select tax code --"
                  />

                  {saveMsg && <span style={{ fontSize: 13, color: T.sageDarker }}>{saveMsg}</span>}

                  <div style={{ borderTop: `1px solid ${T.paperLine}`, paddingTop: 20 }}>
                    <Button
                      variant="delete"
                      size="small"
                      onClick={async () => {
                        if (!confirm('Disconnect MYOB? You will need to reconnect to post receipts.')) return;
                        await api.myobDisconnect();
                        setMyobStatus((s) => s ? { ...s, connected: false, connection: null } : s);
                      }}
                    >
                      Disconnect MYOB
                    </Button>
                  </div>
                </div>
              ) : myobStatus?.configured ? (
                <div>
                  <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 16 }}>
                    Connect your MYOB account to start posting receipts to MYOB.
                  </p>
                  <a
                    href={myobConnectUrl()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 24px',
                      background: '#6B2C91',
                      color: '#FFFFFF',
                      fontSize: 14,
                      fontWeight: 500,
                      textDecoration: 'none',
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#FFFFFF', color: '#6B2C91', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>M</div>
                    Connect to MYOB
                  </a>
                </div>
              ) : (
                <p style={{ fontSize: 14, color: T.graphiteDim, lineHeight: 1.5 }}>
                  MYOB integration is coming soon. Sign up for the Xero path for now; we'll enable MYOB automatically once it's live.
                </p>
              )}
            </div>
          )}

          {/* Email section */}
          {section === 'email' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Email forwarding
              </h2>

              <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 16, lineHeight: 1.5 }}>
                Forward receipt emails to the address below. We match receipts to your account by the sender email.
              </p>

              {/* Forward TO address */}
              <p style={{ ...sx.labelLight, marginBottom: 8 }}>Forward receipts to</p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: T.paper2,
                  borderRadius: 10,
                  border: `1px solid ${T.paperLine}`,
                  marginBottom: 24,
                  flexWrap: isMobile ? 'wrap' : undefined,
                }}
              >
                <Email size={16} color={T.graphiteDim} />
                <code style={{ ...sx.mono, fontSize: 14, color: T.graphite, flex: 1, wordBreak: isMobile ? 'break-all' : undefined }}>
                  {forwardingAddress}
                </code>
                <button
                  onClick={copyEmail}
                  style={{
                    background: '#FFFFFF',
                    border: `1px solid ${T.paperLine}`,
                    borderRadius: 6,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "'Geist', sans-serif",
                    color: T.graphiteMuted,
                    cursor: 'pointer',
                  }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Allowed sender emails */}
              <p style={{ ...sx.labelLight, marginBottom: 12 }}>Allowed sender emails</p>
              <div
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${T.paperLine}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  marginBottom: 16,
                }}
              >
                {/* Always show the Xero login email as the default sender */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderBottom: aliases.length > 0 ? `1px solid ${T.paperLine}` : 'none',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 14, color: T.graphite, wordBreak: 'break-all' }}>
                    {userEmail || 'Loading...'}
                  </span>
                  <span style={{ fontSize: 11, color: T.graphiteDim, fontWeight: 500, flexShrink: 0 }}>
                    Xero login
                  </span>
                </div>
                {aliases.length === 0 ? (
                  <div style={{ padding: '12px 16px', fontSize: 13, color: T.graphiteDim }}>
                    Add aliases below if you also forward from other email addresses.
                  </div>
                ) : (
                  aliases.map((a, i) => (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        borderTop: i > 0 ? `1px solid ${T.paperLine}` : 'none',
                      }}
                    >
                      <span style={{ flex: 1, fontSize: 14, color: T.graphite, wordBreak: 'break-all' }}>{a.email}</span>
                      <button
                        onClick={async () => { await api.deleteEmailAlias(a.id); await reload(); }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: T.graphiteDim,
                          fontSize: 12,
                          padding: 4,
                          minWidth: 44,
                          minHeight: 44,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <FieldInput
                  label=""
                  value={newAlias}
                  onChange={setNewAlias}
                  placeholder="e.g. matt@otherdomain.com"
                  style={{ flex: 1 }}
                />
                <Button
                  variant="navy"
                  size="small"
                  style={{ alignSelf: 'flex-end', marginBottom: 1 }}
                  onClick={async () => {
                    if (!newAlias) return;
                    try {
                      await api.addEmailAlias(newAlias);
                      setNewAlias('');
                      setSaveMsg('Verification email sent. Check your inbox.');
                      await reload();
                    } catch (err) {
                      setSaveMsg((err as Error).message);
                    }
                  }}
                >
                  <Plus size={14} /> Add
                </Button>
              </div>
              {saveMsg && section === 'email' && (
                <p style={{ fontSize: 13, color: T.sageDarker, marginTop: 8 }}>{saveMsg}</p>
              )}
            </div>
          )}

          {/* Automation section */}
          {section === 'automation' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Automation
              </h2>

              {/* Categorisation accuracy dashboard */}
              {metrics && metrics.total > 0 && (
                <div style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 20, marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    <p style={{ ...sx.labelLight, margin: 0 }}>Auto-categorisation accuracy (last {metrics.window_days} days)</p>
                    {metrics.needs_review > 0 && (
                      <a href="#/review" style={{ fontSize: 12, color: T.warn, textDecoration: 'none', fontWeight: 500 }}>
                        {metrics.needs_review} awaiting review →
                      </a>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                    <span style={{ ...sx.serif, fontSize: 44, lineHeight: 1, color: T.graphite, letterSpacing: '-0.02em' }}>
                      {metrics.accuracy != null ? `${Math.round(metrics.accuracy * 100)}%` : '—'}
                    </span>
                    <span style={{ fontSize: 13, color: T.graphiteDim }}>
                      {metrics.unedited} of {metrics.total} receipts posted without an edit
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: T.graphiteDim, marginBottom: 16 }}>
                    <span>Rules: {metrics.sources.rule}</span>
                    <span>·</span>
                    <span>AI: {metrics.sources.llm}</span>
                    <span>·</span>
                    <span>Corpus: {metrics.sources.corpus}</span>
                    <span>·</span>
                    <span>Fuzzy: {metrics.sources.fuzzy}</span>
                  </div>
                  {metrics.top_edited.length > 0 && (
                    <div style={{ borderTop: `1px solid ${T.paperLine}`, paddingTop: 14 }}>
                      <p style={{ ...sx.labelLight, marginBottom: 10 }}>Most-corrected vendors</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {metrics.top_edited.map((v) => (
                          <div key={`${v.vendor}-${v.account_code}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                            <span style={{ color: T.graphite }}>{v.vendor}</span>
                            <span style={{ color: T.graphiteDim, ...sx.mono }}>→ {v.account_code} ({v.count}×)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Refresh chart of accounts */}
              <div style={{ marginBottom: 28, padding: '16px 0', borderBottom: `1px solid ${T.paperLine}` }}>
                <p style={{ ...sx.labelLight, marginBottom: 8 }}>Chart of accounts</p>
                <p style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 12, lineHeight: 1.5 }}>
                  Receipt Agent caches your Xero expense accounts for 24 hours. If you've added a new account in Xero, refresh to pick it up immediately.
                </p>
                <Button variant="ghostLight" size="small" onClick={refreshCoa} disabled={refreshingCoa}>
                  {refreshingCoa ? 'Refreshing…' : 'Refresh from Xero'}
                </Button>
                {coaMsg && <p style={{ fontSize: 12, color: T.graphiteDim, marginTop: 8 }}>{coaMsg}</p>}
              </div>

              {/* Auto-post threshold */}
              <div style={{ marginBottom: 28 }}>
                <p style={{ ...sx.labelLight, marginBottom: 8 }}>Auto-post confidence threshold</p>
                <p style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 12, lineHeight: 1.5 }}>
                  Receipts with AI confidence above this threshold will be posted to Xero automatically.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    step={5}
                    value={autoPostThreshold}
                    onChange={(e) => setAutoPostThreshold(Number(e.target.value))}
                    style={{ flex: 1, accentColor: T.sage }}
                  />
                  <span style={{ ...sx.mono, fontSize: 16, fontWeight: 600, color: T.graphite, minWidth: 48, textAlign: 'right' }}>
                    {autoPostThreshold}%
                  </span>
                </div>
              </div>

              {/* Learn from corrections */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 0',
                  borderTop: `1px solid ${T.paperLine}`,
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, marginBottom: 4 }}>
                    Learn from corrections
                  </p>
                  <p style={{ fontSize: 13, color: T.graphiteDim }}>
                    When you correct an account code, create a rule automatically.
                  </p>
                </div>
                <button
                  onClick={() => setLearnFromCorrections(!learnFromCorrections)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    border: 'none',
                    background: learnFromCorrections ? T.sage : T.paperLine,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 200ms ease',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: learnFromCorrections ? 22 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      transition: 'left 200ms ease',
                    }}
                  />
                </button>
              </div>

              {/* Duplicate detection */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 0',
                  borderTop: `1px solid ${T.paperLine}`,
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, marginBottom: 4 }}>
                    Duplicate detection
                  </p>
                  <p style={{ fontSize: 13, color: T.graphiteDim }}>
                    Flag receipts that look like duplicates before posting.
                  </p>
                </div>
                <button
                  onClick={() => setDuplicateDetection(!duplicateDetection)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    border: 'none',
                    background: duplicateDetection ? T.sage : T.paperLine,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 200ms ease',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: duplicateDetection ? 22 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      transition: 'left 200ms ease',
                    }}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Billing section */}
          {section === 'billing' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Billing
              </h2>
              {!billing ? (
                <p style={{ fontSize: 14, color: T.graphiteDim }}>Loading...</p>
              ) : (
                <>
                  {/* Current plan panel */}
                  <div style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{ padding: '4px 12px', background: billing.plan === 'free' ? T.paper2 : T.sage, borderRadius: 100, fontSize: 12, fontWeight: 600, color: T.navy, textTransform: 'capitalize' }}>
                        {billing.plan}
                      </div>
                      <span style={{ fontSize: 14, color: T.graphite, fontWeight: 500 }}>Current plan</span>
                      {billing.cancel_at_period_end && (
                        <span style={{ fontSize: 12, color: T.warn, fontWeight: 500 }}>Cancels at period end</span>
                      )}
                    </div>

                    {/* Usage bar */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.graphiteDim, marginBottom: 6 }}>
                        <span>{billing.plan === 'free' ? `Lifetime usage` : `This month`}</span>
                        <span style={{ ...sx.mono }}>
                          {billing.plan === 'free' ? `${billing.usage.lifetime} / ${billing.usage.cap}` : `${billing.usage.period} / ${billing.usage.cap}`}
                        </span>
                      </div>
                      <div style={{ height: 6, background: T.paper2, borderRadius: 100, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, ((billing.plan === 'free' ? billing.usage.lifetime : billing.usage.period) / Math.max(1, billing.usage.cap)) * 100)}%`,
                          background: billing.usage.overage > 0 ? T.fail : T.sageDeep,
                          transition: 'width 200ms ease',
                        }} />
                      </div>
                      {billing.usage.overage > 0 && billing.plan !== 'free' && (
                        <p style={{ fontSize: 12, color: T.warn, marginTop: 6 }}>
                          {billing.usage.overage} over your cap. Overage will be added to your next invoice.
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {billing.plan === 'free' ? (
                        <>
                          <Button variant="navy" size="small" onClick={() => startCheckout('solo')} disabled={upgradeBusy !== null}>
                            {upgradeBusy === 'solo' ? 'Loading...' : 'Upgrade to Solo'}
                          </Button>
                          <Button variant="navy" size="small" onClick={() => startCheckout('business')} disabled={upgradeBusy !== null}>
                            {upgradeBusy === 'business' ? 'Loading...' : 'Upgrade to Business'}
                          </Button>
                          <Button variant="ghostLight" size="small" onClick={() => startCheckout('pro')} disabled={upgradeBusy !== null}>
                            {upgradeBusy === 'pro' ? 'Loading...' : 'Upgrade to Pro'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="navy" size="small" onClick={openPortal}>Manage subscription</Button>
                          {billing.plan !== 'pro' && (
                            <Button variant="ghostLight" size="small" onClick={() => startCheckout(billing.plan === 'solo' ? 'business' : 'pro')} disabled={upgradeBusy !== null}>
                              Upgrade to {billing.plan === 'solo' ? 'Business' : 'Pro'}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Plans preview (for free users) */}
                  {billing.plan === 'free' && (
                    <div style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 20 }}>
                      <p style={{ ...sx.labelLight, marginBottom: 12 }}>Plan comparison</p>
                      {[
                        { name: 'Solo', price: '$9.99', receipts: 75, features: 'Core AI + Xero' },
                        { name: 'Business', price: '$19.99', receipts: 300, features: '+ Gmail auto-sync, priority support', highlight: true },
                        { name: 'Pro', price: '$39.99', receipts: 1000, features: '+ Multiple Xero orgs, Outlook (soon)' },
                      ].map((p) => (
                        <div key={p.name} style={{ padding: '12px 0', borderTop: `1px solid ${T.paperLine}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: T.graphite }}>
                              {p.name}
                              {p.highlight && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: T.sage, color: T.navy, borderRadius: 100, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Popular</span>}
                            </div>
                            <div style={{ fontSize: 12, color: T.graphiteDim, marginTop: 2 }}>{p.receipts} receipts / mo · {p.features}</div>
                          </div>
                          <div style={{ ...sx.mono, fontSize: 16, fontWeight: 600, color: T.graphite }}>{p.price}<span style={{ fontSize: 11, color: T.graphiteDim, fontWeight: 400 }}>/mo</span></div>
                        </div>
                      ))}
                      <p style={{ fontSize: 12, color: T.graphiteDim, marginTop: 12, padding: '10px 14px', background: T.paper2, borderRadius: 8 }}>
                        🎟️ Use a promo code at Stripe Checkout if you have one.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Gmail section */}
          {section === 'gmail' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 8 }}>
                Gmail auto-sync
              </h2>
              <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 20, lineHeight: 1.6 }}>
                Connect Gmail and we scan for receipts every hour — attachments are ingested automatically. Read-only access, revoke any time.
              </p>
              {gmail?.connected ? (
                <div style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 100, background: T.sage, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.graphite }}>Connected</div>
                      <div style={{ fontSize: 12, color: T.graphiteDim }}>{gmail.email}</div>
                    </div>
                  </div>
                  {gmail.last_synced_at && (
                    <p style={{ fontSize: 12, color: T.graphiteDim, marginBottom: 16 }}>
                      Last synced {new Date(gmail.last_synced_at * 1000).toLocaleString()}
                    </p>
                  )}
                  <Button
                    variant="ghostLight"
                    size="small"
                    onClick={async () => {
                      if (!confirm('Disconnect Gmail? Auto-sync will stop.')) return;
                      await api.gmailDisconnect();
                      setGmail({ connected: false });
                    }}
                  >
                    Disconnect Gmail
                  </Button>
                </div>
              ) : (
                <div style={{ background: '#FFFFFF', border: `1px solid ${T.paperLine}`, borderRadius: 12, padding: 24 }}>
                  <p style={{ fontSize: 14, color: T.graphite, marginBottom: 16, lineHeight: 1.6 }}>
                    One click to connect. We only read messages with receipt-like attachments and extract the attached PDFs or images.
                  </p>
                  <Button variant="navy" size="small" onClick={() => { window.location.href = gmailConnectUrl(); }}>
                    Connect Gmail
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Profile section */}
          {section === 'profile' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Profile
              </h2>
              <div
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${T.paperLine}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  marginBottom: 20,
                }}
              >
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: `1px solid ${T.paperLine}` }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: T.sage,
                      color: T.navy,
                      fontSize: 16,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {(userEmailProp || userEmail || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, margin: 0 }}>
                      {userEmailProp || userEmail || 'Loading...'}
                    </p>
                    <p style={{ fontSize: 12, color: T.graphiteDim, margin: '2px 0 0' }}>
                      Signed in via Xero
                    </p>
                  </div>
                </div>
                <div style={{ padding: '14px 20px' }}>
                  <button
                    onClick={async () => {
                      const { logout } = await import('../lib/auth');
                      await logout();
                      window.location.href = 'https://receiptrec.com';
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: T.fail,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: "'Geist', sans-serif",
                      padding: 0,
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account section */}
          {section === 'account' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: T.graphite, marginBottom: 20 }}>
                Account
              </h2>

              <div
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${T.fail}`,
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                }}
              >
                <p style={{ ...sx.labelLight, color: T.fail, marginBottom: 16 }}>Danger zone</p>

                <div
                  style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    paddingBottom: 16,
                    borderBottom: `1px solid ${T.paperLine}`,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 12 : 0,
                  }}
                >
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, marginBottom: 4 }}>
                      Export data
                    </p>
                    <p style={{ fontSize: 13, color: T.graphiteDim }}>
                      Download all your receipt data as CSV.
                    </p>
                  </div>
                  <Button variant="ghostLight" size="small">
                    Export
                  </Button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 12 : 0,
                  }}
                >
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, marginBottom: 4 }}>
                      Delete account
                    </p>
                    <p style={{ fontSize: 13, color: T.graphiteDim }}>
                      Permanently delete your account and all data.
                    </p>
                  </div>
                  <Button
                    variant="delete"
                    size="small"
                    onClick={() => {
                      alert('Contact support to delete your account.');
                    }}
                  >
                    Delete account
                  </Button>
                </div>
              </div>
            </div>
          )}
    </>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        <h1 style={{ ...sx.serif, fontSize: 28, color: T.graphite, marginBottom: 20 }}>
          The plumbing.
        </h1>

        {/* Horizontal scrolling pills */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            marginBottom: 24,
            paddingBottom: 4,
          }}
        >
          {NAV_ITEMS.map((item) => {
            const active = section === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSection(item.key)}
                style={{
                  padding: '8px 18px',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: "'Geist', sans-serif",
                  color: active ? '#FFFFFF' : T.graphite,
                  background: active ? T.navy : '#FFFFFF',
                  border: `1px solid ${active ? T.navy : T.paperLine}`,
                  borderRadius: 100,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minHeight: 44,
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Full width content */}
        <div>
          {settingsContent}
        </div>

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ ...sx.serif, fontSize: 32, color: T.graphite, marginBottom: 32 }}>
        The plumbing.
      </h1>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 40 }}>
        {/* Sidebar nav */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_ITEMS.map((item) => {
            const active = section === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSection(item.key)}
                style={{
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  fontFamily: "'Geist', sans-serif",
                  color: active ? T.graphite : T.graphiteDim,
                  background: active ? T.paper2 : 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div style={{ maxWidth: 600 }}>
          {settingsContent}
        </div>
      </div>
    </div>
  );
}
